import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { 
  Download, 
  Package, 
  FileText, 
  Award, 
  CheckCircle, 
  XCircle, 
  Layers, 
  Grid, 
  Construction, 
  Shield, 
  ClipboardList, 
  AlertTriangle, 
  Archive, 
  Tag, 
  Building2, 
  Scale, 
  Info, 
  ShieldCheck 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function IFU() {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Print Button - Hidden in print */}
      <div className="print:hidden fixed top-4 right-4 z-50">
        <Button onClick={handlePrint} className="gap-2">
          <Download className="w-4 h-4" />
          Als PDF speichern
        </Button>
      </div>

      {/* IFU Document */}
      <div className="max-w-4xl mx-auto overflow-hidden bg-card px-4 pb-8 pt-24 sm:p-8 print:p-12">
        {/* Header */}
        <div className="text-center mb-12 border-b-2 border-primary pb-6">
          <button 
            onClick={() => navigate("/")}
            className="print:pointer-events-none inline-block hover:opacity-80 transition-opacity"
            aria-label="Zur Startseite"
          >
            <img 
              src="/images/logo-eap-full.png" 
              alt="EAP Logo" 
              className="h-20 mx-auto mb-4"
            />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            EAP® Abutments – Instructions for Use (IFU)
          </h1>
          <p className="text-lg text-muted-foreground">
            Extended Anatomic Platform
          </p>
        </div>

        {/* 1. Produktbeschreibung */}
        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-start gap-3 break-words">
            <Package className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" />
            1. PRODUKTBESCHREIBUNG
          </h2>
          <div className="space-y-2 text-foreground">
            <h3 className="font-semibold text-lg">EAP® Abutment (Extended Anatomic Platform)</h3>
            <p>Patientenindividuelles Abutment für implantatgetragene Einzelzahn- und Brückenversorgungen.</p>
          </div>
        </section>

        {/* 2. Produktdetails */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <FileText className="w-7 h-7" />
            2. PRODUKTDETAILS
          </h2>
          <div className="space-y-4 text-foreground">
            <p>
              Das EAP® Abutment ist ein patientenspezifisches Abutment mit einer federnden keramischen 
              Auflagekante („spring edge") für präzise Verbindung zwischen Titanbasis und Keramik. 
              Minimiert Adhäsivspalt, schützt periimplantäres Gewebe, ermöglicht ästhetische Integration.
            </p>
            
            <div className="bg-secondary/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Varianten:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>EAP® LOCKED:</strong> Mit Rotationsschutz</li>
                <li><strong>EAP® UNLOCKED:</strong> Ohne Rotationsschutz, ideal für Brückenversorgungen</li>
              </ul>
              <p className="mt-2">Keramische Auflage frei wählbar und austauschbar.</p>
            </div>
          </div>
        </section>

        {/* 3. Klassifikation */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Award className="w-7 h-7" />
            3. KLASSIFIKATION
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Medizinprodukt Klasse IIa (EU MDR 2017/745)</li>
            <li>Sonderanfertigung (Artikel 2 Nr. 3, Artikel 5 Abs. 5 MDR)</li>
          </ul>
        </section>

        {/* 4. Indikation */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <CheckCircle className="w-7 h-7" />
            4. INDIKATION
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Einzelzahn- und Brückenversorgungen auf Bone-Level oder subkrestal gesetzten Implantaten</li>
            <li>Front- und Seitenzahnbereich</li>
            <li>Über Bone-Level möglich bei ausreichendem Weichgewebe</li>
          </ul>
        </section>

        {/* 5. Kontraindikationen */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <XCircle className="w-7 h-7" />
            5. KONTRAINDIKATIONEN
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Tissue-Level-Implantate</li>
            <li>Unzureichendes Weichgewebe, Entzündungen</li>
            <li>Allergien gegen Titan/Zirkonoxid</li>
            <li>Nicht osseointegrierte/mobile Implantate</li>
          </ul>
        </section>

        {/* 6. Materialien */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Layers className="w-7 h-7" />
            6. MATERIALIEN
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li><strong>Titanbasis:</strong> Reintitan Grad 4 / Ti6Al4V ELI</li>
            <li><strong>Keramische Auflage:</strong> Zirkonoxid (Y-TZP)</li>
            <li><strong>Adhäsivsystem:</strong> Anwendung durch Zahntechniker oder Zahnarzt möglich</li>
          </ul>
        </section>

        {/* 7. Varianten */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Grid className="w-7 h-7" />
            7. VARIANTEN
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <thead>
                <tr className="bg-secondary/20">
                  <th className="border border-border p-3 text-left font-semibold">Variante</th>
                  <th className="border border-border p-3 text-left font-semibold">Beschreibung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-3">EAP® LOCKED</td>
                  <td className="border border-border p-3">Mit Rotationsschutz</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">EAP® UNLOCKED</td>
                  <td className="border border-border p-3">Ohne Rotationsschutz – ideal für Brücken</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 8. Herstellung */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Construction className="w-7 h-7" />
            8. HERSTELLUNG
          </h2>
          <ol className="space-y-2 text-foreground list-decimal list-inside">
            <li>Abdruck analog oder digital (STL-Datei)</li>
            <li>CAD-Planung durch Zahntechniker</li>
            <li>Upload in EAP® Shop</li>
            <li>Patientenspezifische Fertigung</li>
            <li>Konformitäts- und Sonderanfertigungs-Erklärung</li>
            <li>Lieferung an Zahntechniker/Zahnarzt</li>
          </ol>
          <p className="mt-2 text-foreground">Herstellungsprozess validiert.</p>
        </section>

        {/* 9. Aufbereitung / Sterilisation */}
        <section className="mb-8 bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Shield className="w-7 h-7" />
            9. AUFBEREITUNG / STERILISATION
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li><strong>Produkt wird unsteril geliefert</strong></li>
            <li>Vor klinischem Einsatz im Dampfdrucksterilisator (Autoklav) bei <strong>121 °C sterilisieren</strong></li>
          </ul>
        </section>

        {/* 10. Anwendungshinweise */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <ClipboardList className="w-7 h-7" />
            10. ANWENDUNGSHINWEISE
          </h2>
          <ol className="space-y-2 text-foreground list-decimal list-inside">
            <li>Implantatfreilegung nach Osseointegration</li>
            <li>Reinigung des Implantatinnenraums</li>
            <li>H-Cut und Verschiebelappen präparieren</li>
            <li>Abutment einsetzen, Schraube anziehen, Weichgewebe adaptieren</li>
            <li>Nachträgliche Korrektur: Abutment entfernen, Titanhülle polieren/fräsen, wieder einsetzen (~5 Min)</li>
          </ol>
        </section>

        {/* 11. Warnhinweise */}
        <section className="mb-8 bg-red-50 border-2 border-red-400 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4 border-b border-red-400 pb-2 flex items-center gap-3">
            <AlertTriangle className="w-7 h-7" />
            11. WARNHINWEISE
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Nur durch Zahnarzt/Zahntechniker anwenden</li>
            <li>Nicht mit fremden Komponenten kombinieren</li>
            <li>Beschädigte Abutments nicht einsetzen</li>
            <li>Nur für die vorgesehene Anwendung geeignete Adhäsive verwenden</li>
            <li>Änderungen nur durch EAP® GmbH</li>
          </ul>
        </section>

        {/* 12. Aufbewahrung */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Archive className="w-7 h-7" />
            12. AUFBEWAHRUNG
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Trocken, staubfrei, 10–30 °C</li>
            <li>Vor UV-Strahlung und Feuchtigkeit schützen</li>
            <li>Originalverpackung verwenden</li>
          </ul>
        </section>

        {/* 13. Kennzeichnung */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Tag className="w-7 h-7" />
            13. KENNZEICHNUNG
          </h2>
          <p className="text-foreground">
            Sonderanfertigung gemäß MDR Artikel 2 Nr.3 & Artikel 5 Abs.5. 
            Produkt nur für genannten Patienten.
          </p>
        </section>

        {/* 14. Hersteller */}
        <section className="mb-8 bg-secondary/20 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Building2 className="w-7 h-7" />
            14. HERSTELLER
          </h2>
          <div className="space-y-1 text-foreground">
            <p className="font-semibold">EAP® Produktions- und Patentverwertungs-GmbH</p>
            <p>Geschäftsführer: Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.</p>
            <p>Lorettostraße 26, 2. + 3. Stock</p>
            <p>6060 Hall in Tirol, Austria</p>
            <p>FB-Gericht Innsbruck, Firmenbuchnummer 483289w</p>
            <p>UID: ATU72852709</p>
            <p className="mt-2">
              <a href="https://eap-abutments.com" className="text-primary hover:underline">
                eap-abutments.com
              </a>
            </p>
          </div>
        </section>

        {/* 15. Rechtliche Hinweise */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Scale className="w-7 h-7" />
            15. RECHTLICHE HINWEISE
          </h2>
          <ul className="space-y-2 text-foreground list-disc list-inside">
            <li>Herstellungsprozess validiert</li>
            <li>Konformitätserklärung liegt bei</li>
            <li>Patientendaten DSGVO-konform</li>
          </ul>
        </section>

        {/* 16. Symbolerklärung */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <Info className="w-7 h-7" />
            16. SYMBOLERKLÄRUNG
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <thead>
                <tr className="bg-secondary/20">
                  <th className="border border-border p-3 text-left font-semibold">Symbol</th>
                  <th className="border border-border p-3 text-left font-semibold">Bedeutung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">🏭</td>
                  <td className="border border-border p-3">Hersteller</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">⚠️</td>
                  <td className="border border-border p-3">Warnhinweis</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">📦</td>
                  <td className="border border-border p-3">Einmalprodukt</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">⛔</td>
                  <td className="border border-border p-3">Nicht zur Wiederverwendung</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">🧴</td>
                  <td className="border border-border p-3">Sterilisation erforderlich vor Gebrauch</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">📅</td>
                  <td className="border border-border p-3">Herstellungsdatum</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">🦷</td>
                  <td className="border border-border p-3">Dentalanwendung</td>
                </tr>
                <tr>
                  <td className="border border-border p-3 text-center text-2xl">🧾</td>
                  <td className="border border-border p-3">Sonderanfertigung gemäß MDR</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 17. Konformität */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-border pb-2 flex items-center gap-3">
            <ShieldCheck className="w-7 h-7" />
            17. KONFORMITÄT
          </h2>
          <p className="text-foreground">
            Herstellung und Lieferung gemäß EU MDR 2017/745, Anhang XIII, Abschnitt 1 (Sonderanfertigungen). 
            Konformitätserklärung bestätigt den validierten Herstellungsprozess und die patientenspezifische Sonderanfertigung.
          </p>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t-2 border-primary text-center text-sm text-muted-foreground">
          <p>© 2017-{new Date().getFullYear()} EAP® Produktions- und Patentverwertungs-GmbH</p>
          <p className="mt-2">Alle Rechte vorbehalten.</p>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media (max-width: 639px) {
          section h2 {
            overflow-wrap: anywhere;
          }
        }
        @media print {
          @page {
            margin: 2cm;
            size: A4;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:p-12 {
            padding: 0 !important;
          }
          
          section {
            page-break-inside: avoid;
          }
          
          h2 {
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
